import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getGiveaway } from "../services/giveawayService";
import { handleUnauthorized } from "../../utils/apiHelper";
import { apiProblem, isSuperAdmin, PHASE_LABELS } from "./giveawayUtils";
import { StatusPill } from "./GiveawayUi";
import NotAllowedCard from "./NotAllowedCard";
import GiveawayOverviewTab from "./GiveawayOverviewTab";
import GiveawaySettingsTab from "./GiveawaySettingsTab";
import GiveawayEntriesTab from "./GiveawayEntriesTab";
import GiveawayDrawTab from "./GiveawayDrawTab";
import GiveawayAuditTab from "./GiveawayAuditTab";

// One giveaway, five tabs. The giveaway is loaded once here and handed to
// every tab; any tab that changes something calls reload() so the counts,
// checklist and draw state everywhere stay in step with the server.

const TABS = [
  { key: "overview", label: "Overview", icon: "mdi:view-dashboard-outline" },
  { key: "settings", label: "Settings", icon: "mdi:cog-outline" },
  { key: "entries", label: "Entries", icon: "mdi:account-multiple-outline" },
  { key: "draw", label: "Draw and winner", icon: "mdi:trophy-outline" },
  { key: "audit", label: "Audit", icon: "mdi:history" },
];

const GiveawayDetailLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);
  const [tab, setTab] = useState("overview");

  const onUnauthorized = useCallback(() => handleUnauthorized(navigate), [navigate]);

  const reload = useCallback(async () => {
    setProblem(null);
    try {
      const res = await getGiveaway(id);
      setG(res.data?.data || null);
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      setProblem(apiProblem(e, "Could not load this giveaway."));
    } finally {
      setLoading(false);
    }
  }, [id, onUnauthorized]);

  useEffect(() => {
    if (isSuperAdmin()) reload();
  }, [reload]);

  if (!isSuperAdmin() || problem?.status === 403) {
    return <NotAllowedCard message={problem?.message} />;
  }

  if (loading && !g) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24 text-secondary-light">Loading…</div>
      </div>
    );
  }

  if (!g) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <div className="alert alert-danger py-2 mb-16">{problem?.message || "This giveaway was not found."}</div>
          <Link to="/giveaways" className="btn btn-outline-primary btn-sm">
            Back to Giveaways
          </Link>
        </div>
      </div>
    );
  }

  const shared = { g, reload, onUnauthorized };

  return (
    <div>
      <div className="card radius-12 mb-24">
        <div className="card-body p-24 d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div style={{ minWidth: 0 }}>
            <Link to="/giveaways" className="text-sm text-secondary-light d-inline-flex align-items-center gap-1 mb-8">
              <Icon icon="mdi:arrow-left" /> All giveaways
            </Link>
            <h5 className="fw-semibold mb-1">{g.name || g.slug}</h5>
            <div className="text-sm text-secondary-light">
              {g.city || "No city"} · {g.slug}
            </div>
          </div>
          <div className="text-end">
            <StatusPill status={g.status} paused={g.paused} />
            <div className="text-xs text-secondary-light mt-1">{PHASE_LABELS[g.phase] || g.phase}</div>
          </div>
        </div>
      </div>

      {problem ? <div className="alert alert-danger py-2">{problem.message}</div> : null}

      <ul className="nav border-gradient-tab nav-pills mb-20 d-flex flex-wrap" role="tablist">
        {TABS.map((t) => (
          <li className="nav-item" role="presentation" key={t.key}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`nav-link d-flex align-items-center gap-2 px-24 ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <Icon icon={t.icon} />
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === "overview" && <GiveawayOverviewTab {...shared} goTo={setTab} />}
      {tab === "settings" && <GiveawaySettingsTab {...shared} />}
      {tab === "entries" && <GiveawayEntriesTab {...shared} />}
      {tab === "draw" && <GiveawayDrawTab {...shared} />}
      {tab === "audit" && <GiveawayAuditTab {...shared} />}
    </div>
  );
};

export default GiveawayDetailLayer;
